import { FontWeight, Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ButtonColor, ButtonHeight, ButtonPadding, ButtonSize, ButtonType } from 'constants/button';
import { HTMLProps, ReactNode, useMemo } from 'react';

type Modify<T, R> = Omit<T, keyof R> & R;

export type ButtonProps = Modify<
  HTMLProps<HTMLButtonElement>,
  {
    className?: string;
    type?: 'button' | 'submit' | 'reset';
    label: string | ReactNode;
    buttonType?: ButtonType;
    size?: ButtonSize;
    color?: string;
    buttonTypography?: string;
    fontWeight?: number;
    fullWidth?: boolean;
  }
>;

export const Button = ({ type = 'button', label, buttonType = ButtonType.Primary, as, ...props }: ButtonProps) => {
  const { backgroundColor, color, borderColor } = useMemo(() => ButtonColor[buttonType], [buttonType]);

  return (
    <StyledButton type={type} backgroundColor={backgroundColor} color={color} borderColor={borderColor} {...props}>
      {label}
    </StyledButton>
  );
};

export const StyledButton = styled.button(
  ({
    backgroundColor,
    color,
    borderColor,
    size = ButtonSize.Medium,
    buttonTypography = Typography.XXSmall,
    fontWeight = FontWeight.Bold,
    fullWidth,
    disabled,
  }: {
    backgroundColor: string;
    color: string;
    borderColor: string;
    size?: ButtonSize;
    disabled?: boolean;
    buttonTypography?: string;
    fontWeight?: number;
    fullWidth?: boolean;
  }) => css`
    background-color: ${backgroundColor};
    color: ${color};
    border: 1px solid ${borderColor};
    height: ${ButtonHeight[size]}px;
    padding: 0 ${ButtonPadding[size].horizontal}px;
    ${buttonTypography};
    font-weight: ${fontWeight};
    width: ${fullWidth ? '100%' : 'auto'};

    display: flex;
    align-items: center;
    justify-content: center;

    white-space: nowrap;
    border-radius: 4px;

    &:active {
      filter: brightness(0.9);
    }

    &:disabled {
      cursor: default;
      filter: brightness(0.9);
    }

    opacity: ${disabled ? '0.3' : 'none'};
  `,
);
